import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { queryKeys } from "@/lib/queryKeys";
import { useRealtimeCollection } from "@/lib/realtime";
import { copyText } from "@/lib/clipboard";
import { playOnRise } from "@/platform";
import { useToast } from "@/ui";
import {
  answerCard,
  closeCard,
  decideCard,
  fetchCards,
  fetchCategories,
  fetchComments,
  fetchReviewCount,
  fetchSharedCards,
  fetchSharedCategories,
  fetchSharedOwners,
  fetchVersions,
  queueCard,
  releaseCard,
  reopenCard,
  setCategoryArchived,
  unqueueCard,
  type DecideInput,
  type QueueCardInput,
} from "./api";
import { pickCommand, type FlowCard } from "./model";

const CARDS = queryKeys.collection("flow_cards", "list");
const COUNT = queryKeys.collection("flow_cards", "review-count");
const BADGE_POLL_MS = 60_000;

export function useFlowCategories() {
  useRealtimeCollection("flow_categories");
  return useQuery({
    queryKey: queryKeys.collection("flow_categories"),
    queryFn: ({ signal }) => fetchCategories(signal),
    refetchOnMount: "always",
  });
}

export function useFlowCards() {
  useRealtimeCollection("flow_cards");
  const client = useQueryClient();
  const query = useQuery({ queryKey: CARDS, queryFn: ({ signal }) => fetchCards(signal), refetchOnMount: "always" });
  // The list just saw the server, so the badge must not go on disagreeing with it.
  const fetchedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (fetchedAt) void client.invalidateQueries({ queryKey: COUNT });
  }, [client, fetchedAt]);
  return query;
}

/**
 * Other org members' cutter work (categories a Worker picks), read-only: their categories, their
 * cards and their names. Keyed under the same collections, so realtime keeps them fresh. Empty
 * for an account outside the org.
 */
export function useSharedFlows() {
  useRealtimeCollection("flow_cards");
  useRealtimeCollection("flow_categories");
  const categories = useQuery({ queryKey: queryKeys.collection("flow_categories", "shared"), queryFn: ({ signal }) => fetchSharedCategories(signal), refetchOnMount: "always" });
  const cards = useQuery({ queryKey: queryKeys.collection("flow_cards", "shared"), queryFn: ({ signal }) => fetchSharedCards(signal), refetchOnMount: "always" });
  // Names change rarely; a new member's lane brings its owner with the next categories refresh.
  const owners = useQuery({ queryKey: queryKeys.collection("flow_categories", "shared-owners"), queryFn: ({ signal }) => fetchSharedOwners(signal) });
  return { categories, cards, owners };
}

export function useFlowVersions(card: string) {
  useRealtimeCollection("flow_versions");
  return useQuery({
    queryKey: queryKeys.collection("flow_versions", card),
    queryFn: ({ signal }) => fetchVersions(card, signal),
    // Mounted only while a card is open: a version that landed while it was
    // closed had no subscriber to invalidate this.
    refetchOnMount: "always",
  });
}

export function useFlowComments(card: string) {
  useRealtimeCollection("flow_comments");
  return useQuery({
    queryKey: queryKeys.collection("flow_comments", card),
    queryFn: ({ signal }) => fetchComments(card, signal),
    refetchOnMount: "always",
  });
}

function useCardMutation<Input, Output>(mutationFn: (input: Input) => Promise<Output>, sources = false) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.collection("flow_cards") });
      void client.invalidateQueries({ queryKey: queryKeys.collection("flow_comments") });
      // Queueing hides the Task or archives the Inbox item it came from, and
      // taking it back restores them; approving can tick the Task too.
      if (sources) {
        void client.invalidateQueries({ queryKey: queryKeys.collection("flow_categories") });
        void client.invalidateQueries({ queryKey: queryKeys.collection("tasks") });
        void client.invalidateQueries({ queryKey: queryKeys.collection("inbox_items") });
      }
    },
  });
}

export const useDecideCard = () => useCardMutation((input: DecideInput) => decideCard(input), true);
export const useReopenCard = () => useCardMutation((card: string) => reopenCard(card));
export const useReleaseCard = () => useCardMutation((card: string) => releaseCard(card));
// Closing puts a queued Task or Inbox item back where it was.
export const useCloseCard = () => useCardMutation((input: { card: string; reason: string }) => closeCard(input), true);
export const useAnswerCard = () => useCardMutation((input: { card: string; body: string }) => answerCard(input));
export const useQueueCard = () => useCardMutation((input: QueueCardInput) => queueCard(input), true);
export const useUnqueueCard = () => useCardMutation((card: string) => unqueueCard(card), true);

export function useArchiveCategory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => setCategoryArchived(id, archived),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.collection("flow_categories") });
      void client.invalidateQueries({ queryKey: queryKeys.collection("flow_cards") });
    },
  });
}

/** The nav badge, which is also what chimes when a new card lands while the app is open. */
export function useFlowsBadgeCount(): number {
  useRealtimeCollection("flow_cards");
  // Polled as well: the app stays open for days, and a card that lands before a
  // subscription connects, or during a dropped one, sends no event at all.
  const query = useQuery({
    queryKey: COUNT,
    queryFn: ({ signal }) => fetchReviewCount(signal),
    refetchInterval: BADGE_POLL_MS,
  });
  const count = query.data ?? 0;
  useEffect(() => {
    if (query.isSuccess) playOnRise("flows:review", count, "notify");
  }, [count, query.isSuccess]);
  return count;
}

/** Copies the line Pipe pastes into a chat so an agent takes this card (model.ts pickCommand). */
export function useCopyCommand(card: Pick<FlowCard, "id" | "title" | "status">) {
  const toast = useToast();
  return async () => {
    if (await copyText(pickCommand(card))) toast.show("คัดลอกคำสั่งแล้ว เอาไปวางในแชทได้เลย");
    else toast.error("คัดลอกไม่ได้");
  };
}
